import { animate, motion, type PanInfo, useDragControls, useMotionValue } from "framer-motion";
import { ChevronDownIcon, ChevronUpIcon, XIcon } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

/** Same spring family as FloatingCard so the two surfaces feel related. */
const SPRING = { type: "spring", stiffness: 420, damping: 38 } as const;

/** Release speed (px/s) above which a gesture reads as a deliberate flick. */
const FLICK_SPEED = 300;

interface CallDrawerProps {
  "aria-label": string;
  /**
   * Compact summary row rendered beside the drawer chrome. It stays visible
   * while minimized, so it should read as a status bar on its own (e.g.
   * caller name + duration).
   */
  header: ReactNode;
  minimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  /**
   * Renders a close (X) button in the chrome. Omit for surfaces that must
   * stay open for the session — e.g. the active-call widget, which only
   * ends via its End call button.
   */
  onClose?: () => void;
  /**
   * Compact actions pinned to the right of the header bar, before the
   * expand/collapse chevron. They stay reachable while the drawer is docked
   * (e.g. mute + hang up on an active call), so the essential controls don't
   * disappear when the body is hidden. Wrap interactive elements' handlers
   * yourself; the drawer already marks this slot `data-no-drag` so taps here
   * never start a drag or toggle the drawer.
   */
  headerActions?: ReactNode;
  children: ReactNode;
}

/**
 * Mobile counterpart to FloatingCard: a bottom drawer pinned above all
 * content (same dark call-surface treatment). Dragging down docks it to a
 * compact header bar at the bottom edge — it can never be drag-dismissed —
 * and dragging up (or tapping the bar) restores it.
 */
export function CallDrawer({
  "aria-label": ariaLabel,
  header,
  minimized,
  onMinimizedChange,
  onClose,
  headerActions,
  children,
}: CallDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const y = useMotionValue(0);
  const dragControls = useDragControls();
  /** How far the panel translates down so only the header bar peeks out. */
  const [dockOffset, setDockOffset] = useState(0);
  const draggedRef = useRef(false);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const headerEl = headerRef.current;
    if (!panel || !headerEl) return;
    const measure = () => setDockOffset(Math.max(0, panel.offsetHeight - headerEl.offsetHeight));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  // Keep the panel snapped to its state when it changes from the chrome
  // buttons or when content resizes under it (e.g. keypad toggled).
  useEffect(() => {
    const controls = animate(y, minimized ? dockOffset : 0, SPRING);
    return () => controls.stop();
  }, [y, minimized, dockOffset]);

  const onDragEnd = useCallback(
    (_event: unknown, info: PanInfo) => {
      const next =
        info.velocity.y > FLICK_SPEED
          ? true
          : info.velocity.y < -FLICK_SPEED
            ? false
            : y.get() > dockOffset / 2;
      onMinimizedChange(next);
      animate(y, next ? dockOffset : 0, { ...SPRING, velocity: y.getVelocity() });
    },
    [y, dockOffset, onMinimizedChange],
  );

  return createPortal(
    // Outer layer owns enter/exit (slide from the bottom edge); the inner
    // panel owns the dock translation, so the two animations never fight.
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      exit={{ y: "110%" }}
      transition={SPRING}
      // Sits above the mobile tab bar (route-scoped --app-tab-bar-h) so the
      // docked call header and the tab navigation stay usable together; the
      // bar is md:hidden, so pin back to the viewport bottom from md up.
      className="pointer-events-none fixed inset-x-0 bottom-[var(--app-tab-bar-h,0px)] z-[60] md:bottom-0"
    >
      <motion.div
        ref={panelRef}
        role="dialog"
        aria-label={ariaLabel}
        drag="y"
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={{ top: 0, bottom: dockOffset }}
        dragElastic={{ top: 0.04, bottom: 0 }}
        dragMomentum={false}
        onDragStart={() => {
          draggedRef.current = true;
        }}
        onDragEnd={onDragEnd}
        style={{ y }}
        className="pointer-events-auto rounded-t-2xl border-x border-t border-white/10 bg-zinc-950/95 pb-[env(safe-area-inset-bottom)] text-zinc-50 shadow-2xl backdrop-blur"
      >
        <div
          ref={headerRef}
          onPointerDown={(event) => {
            if ((event.target as HTMLElement).closest("[data-no-drag]")) return;
            draggedRef.current = false;
            dragControls.start(event);
          }}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("[data-no-drag]")) return;
            if (minimized && !draggedRef.current) onMinimizedChange(false);
          }}
          className="cursor-grab touch-none select-none active:cursor-grabbing"
        >
          <div className="flex justify-center pt-2" aria-hidden="true">
            <span className="h-1 w-9 rounded-full bg-white/20" />
          </div>
          <div className="flex items-center gap-2 px-4 pb-3 pt-1.5">
            <div className="min-w-0 flex-1">{header}</div>
            {headerActions && (
              <div data-no-drag className="flex shrink-0 items-center gap-1.5">
                {headerActions}
              </div>
            )}
            <button
              type="button"
              data-no-drag
              onClick={() => onMinimizedChange(!minimized)}
              aria-label={minimized ? "Expand" : "Minimize"}
              className="flex size-8 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-zinc-50"
            >
              {minimized ? (
                <ChevronUpIcon className="size-4" />
              ) : (
                <ChevronDownIcon className="size-4" />
              )}
            </button>
            {onClose && (
              <button
                type="button"
                data-no-drag
                onClick={onClose}
                aria-label="Close"
                className="flex size-8 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-zinc-50"
              >
                <XIcon className="size-4" />
              </button>
            )}
          </div>
        </div>
        <div
          aria-hidden={minimized}
          className={cn(
            "max-h-[70dvh] overflow-y-auto px-4 pb-4",
            minimized && "pointer-events-none",
          )}
        >
          {children}
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
